/**
 * Single source for the two store URLs and the web→app attribution that
 * rides on them. Pure module (no DOM, no Node APIs) so the React shells, the
 * SSR bundle and every scripts/*.mjs page generator build identical links.
 *
 * Attribution:
 *   Google Play — `referrer=` carries UTM params; Firebase reads the Play
 *     Install Referrer automatically, so first_open is attributed to the
 *     website and to the page type the visitor clicked from.
 *   App Store   — `pt` (provider token) + `ct` (campaign token) + `mt=8`.
 *     App Analytics only records `ct` when `pt` is present, so the links
 *     carry `pt` as soon as APP_STORE_PROVIDER_TOKEN below is filled in.
 *
 * `pageType` is a short slug for where the link lives (home, country-home,
 * test-centre, blog, pillar, best-apps, …). It also goes on <body
 * data-page-type> so the store_click GA4 event (see
 * scripts/_store-click-snippet.mjs) reports the same value.
 */

export const IOS_APP_ID = '6762903623';
export const ANDROID_PACKAGE = 'com.steerclear';

export const APP_STORE_URL = `https://apps.apple.com/gb/app/steerclear-driving-practice/id${IOS_APP_ID}`;
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

// App Store Connect provider token for campaign links (App Store Connect →
// app → Analytics → Acquisition → Campaigns → Generate a Campaign Link; the
// `pt=` value is the same for every campaign on the STEERCLEAR LTD account).
// Each page type shows up in App Analytics as its own campaign
// (`steerclear-web-<page_type>`) once 5+ Apple Accounts have installed via it.
export const APP_STORE_PROVIDER_TOKEN = '128812774';

export const UTM_SOURCE = 'steerclear.io';
export const UTM_MEDIUM = 'web';

/** Lower-case [a-z0-9-] slug; falls back to 'web' for empty input. */
export function pageTypeSlug(pageType) {
  const slug = String(pageType || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'web';
}

/** App Store link with campaign attribution for the given page type. */
export function appStoreUrl(pageType) {
  const params = [];
  if (APP_STORE_PROVIDER_TOKEN) params.push(`pt=${encodeURIComponent(APP_STORE_PROVIDER_TOKEN)}`);
  params.push(`ct=steerclear-web-${pageTypeSlug(pageType)}`, 'mt=8');
  return `${APP_STORE_URL}?${params.join('&')}`;
}

/** Google Play link with an Install Referrer UTM payload for the page type. */
export function playStoreUrl(pageType) {
  const referrer = encodeURIComponent(
    `utm_source=${UTM_SOURCE}&utm_medium=${UTM_MEDIUM}&utm_campaign=${pageTypeSlug(pageType)}`,
  );
  return `${PLAY_STORE_URL}&pcampaignid=web_share&referrer=${referrer}`;
}

export function storeUrl(store, pageType) {
  return store === 'ios' ? appStoreUrl(pageType) : playStoreUrl(pageType);
}

/**
 * 'ios' | 'android' | 'other' from a navigator-like object. Mirrors the
 * inline PLATFORM_SNIPPET that classes <html> before first paint, so the
 * React bar and the CSS toggles always agree.
 */
export function detectPlatform(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return 'other';
  const ua = nav.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

// ── Sticky mobile download bar ───────────────────────────────────────────────
export const STICKY_DISMISS_KEY = 'sc_sticky_dismissed';
export const STICKY_DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * The bar may show once the visitor has scrolled this far — and only while no
 * in-page store CTA (hero buttons, a CTA block) is in the viewport, so it never
 * doubles up with a button already on screen. Pages whose only CTA sits far
 * down (long guides) therefore get the bar after ~400px instead of at the end.
 */
export const STICKY_MIN_SCROLL_PX = 400;
/** Desktop (>= this width) never shows the bar; matches the lg breakpoint. */
export const STICKY_MAX_WIDTH_PX = 1023;

/** True when the visitor dismissed the bar less than 7 days ago. */
export function stickyDismissed(storage, now = Date.now()) {
  try {
    const at = Number(storage?.getItem(STICKY_DISMISS_KEY) || 0);
    return at > 0 && now - at < STICKY_DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Button copy. English pages use the exact house copy; other languages lead
 * with that language's "Get SteerClear" string and name the store after it.
 */
export function stickyLabels(lead) {
  const english = !lead || lead === 'Get SteerClear';
  return {
    ios: english ? 'Get SteerClear on the App Store' : `${lead} · App Store`,
    play: english ? 'Get SteerClear on Google Play' : `${lead} · Google Play`,
    iosShort: 'App Store',
    playShort: 'Google Play',
    close: 'Dismiss',
  };
}
